import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { map, Observable, Subject, take, takeUntil } from 'rxjs';
import { BaseService } from '../../../../services/base.service';
import { OlxAdvertService } from '../../../../services/olx-advert.service';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';
import { CHANNEL_STATUS, CHANNEL_TYPE, activeTranslations } from '../../../../enum/constants';
import { IParams } from '../../../../model/http';
import { ChannelService } from '../../../../services/channel.service';

@Component({
  selector: 'app-my-deliveries',
  templateUrl: './my-deliveries.component.html',
  styleUrl: './my-deliveries.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MyDeliveriesComponent implements OnInit, OnDestroy {
  private readonly destroy$: Subject<null> = new Subject<null>();

  constructor(
    public readonly baseService: BaseService,
    private readonly olxAdvertService: OlxAdvertService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly channelService: ChannelService,
  ) {}

  public isLoader: boolean = false;
  public items: any[] = [];
  public itemsRequest: any;
  public currentChannelId: any = null;

  private channelQuery = this.activatedRoute.snapshot.queryParams['channel'];

  public filterStatuses = [
    { name: 'olx.delivery.status_short.accepted', value: 'accepted', color: 'green', active: true, selected: false },
    { name: 'olx.delivery.status_short.waiting', value: 'waiting', color: 'gray', active: true, selected: false },
    { name: 'olx.delivery.status_short.rejected', value: 'rejected', color: 'red', active: true, selected: false },
  ];

  dataSource$: Observable<any> = this.olxAdvertService.itemsDeliveries$.pipe(
    map((data) => {
      return {
        items: data.entities,
        pageSize: 10,
        pageIndex: data.page ? data.page : 0,
        length: data.total || 0,
      };
    }),
  );

  getItemsWithUpdatedParams(params: IParams, isLoader = false) {
    if (this.itemsRequest) {
      this.itemsRequest.unsubscribe();
    }
    this.isLoader = isLoader;
    this.itemsRequest = this.olxAdvertService
      .setQueryDeliveryParams(this.currentChannelId, params)
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: () => {
          this.isLoader = false;
        },
        error: () => {
          this.isLoader = false;
        },
      });
  }

  onFilterChange(item: any) {
    if (item.selected) {
      this.filterStatuses.forEach((v) => (v.active = true));
      this.filterStatuses.forEach((v) => (v.selected = false));
      this.getItemsWithUpdatedParams({ page: 1, filters: '' }, true);
    } else {
      this.filterStatuses.forEach((v) => (v.active = false));
      this.filterStatuses.forEach((v) => (v.selected = false));
      item.active = true;
      item.selected = true;
      this.getItemsWithUpdatedParams({ page: 1, filters: item.value }, true);
    }
  }

  ngOnInit() {
    this.isLoader = true;
    this.channelService
      .getChannels({ scope: 'apps', types: [CHANNEL_TYPE.OLX], status: CHANNEL_STATUS.CONNECTED })
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe({
        next: (channels: any[]) => {
          this.items = channels;
          const data = {
            id: this.channelQuery || channels[0]?.id || null,
          };
          this.onValueChange(data);
        },
        error: () => {
          this.isLoader = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.olxAdvertService.destroy();
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  onValueChange(event: any) {
    this.currentChannelId = event?.id;
    this.baseService.setQuery('channel', this.currentChannelId);
    this.getItemsWithUpdatedParams({}, true);
  }

  getStatus(item: any) {
    const delivery = activeTranslations.olx.delivery;
    let status: string = delivery.status[item.statusShort][item.status];
    if (!status) status = delivery.status.data_error;
    const needsExpiration = ['accepted', 'money_blocked'].includes(item.status);
    if (needsExpiration && item.expiresAt) {
      status += moment(item.expiresAt).format('D MMMM, HH:mm');
    }
    return status;
  }

  getStatusColor(status: string): string {
    const foundStatus = this.filterStatuses.find((s) => s.value === status);
    return foundStatus ? foundStatus.color : 'red';
  }

  onPaginationChange(paginationDetails: any) {
    const { pageIndex } = paginationDetails;
    const page = pageIndex;
    this.getItemsWithUpdatedParams({ page }, true);
  }
}
